/* global HTMLDivElement, KeyboardEvent */
// What's New feature spotlight modal (A8).
// Shows once per app version when the user has already completed onboarding.
// Hardcoded P2 highlights in Vietnamese; closed via "Đã hiểu" / "Khám phá".
import { useEffect, useRef } from "react";
import {
  BookMarked,
  BookOpen,
  CheckCircle2,
  Clock,
  Goal,
  Inbox,
  Keyboard,
  Sparkles,
  Sun,
  Undo2,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useFocusTrap } from "../lib/focus-trap";

interface HighlightItem {
  icon: LucideIcon;
  label: string;
}

// P2 highlights — hardcoded, Vietnamese, ~8-10 items max (YAGNI).
const HIGHLIGHTS: HighlightItem[] = [
  { icon: BookMarked,  label: "Nhật ký — đọc lại kế hoạch & tổng kết theo ngày" },
  { icon: Clock,       label: "Review tuần / tháng — tổng hợp tiến độ định kỳ" },
  { icon: CheckCircle2,label: "Việc lặp lại — sinh tự động theo lịch RRULE" },
  { icon: Goal,        label: "OKR nâng cao — Key Results với % hoàn thành" },
  { icon: Inbox,       label: "Inbox Someday / Đang chờ — đỗ mục không khẩn" },
  { icon: Sparkles,    label: "Gõ nhanh ngôn ngữ tự nhiên — \"Họp sếp ngày mai +high\"" },
  { icon: BookOpen,    label: "Đồ thị ghi chú — xem backlinks [[liên kết]] trực quan" },
  { icon: Undo2,       label: "Hoàn tác khi xoá — phục hồi trong 5 giây" },
  { icon: Keyboard,    label: "Phím tắt — /, n, g, ? và Esc để điều hướng nhanh" },
  { icon: Sun,         label: "Sức chứa ngày — thanh tiến độ MIT & giờ làm" },
];

interface Props {
  version: string;
  onClose: () => void;
}

/**
 * WhatsNewModal — version-aware feature spotlight.
 * Props:
 *   version — current APP_VERSION string (displayed in header)
 *   onClose — called when user dismisses; caller should then setLastSeenVersion
 */
export function WhatsNewModal({ version, onClose }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);

  // Escape closes the modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
    >
      <div
        ref={panelRef}
        className="fade-in relative w-full max-w-md rounded-3xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          className="absolute right-4 top-4 rounded-xl p-1.5 hover:bg-ink-100"
          onClick={onClose}
          aria-label="Đóng"
        >
          <X className="h-4 w-4 text-ink-500" />
        </button>

        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-brand-100 text-brand-600">
            <Sparkles className="h-6 w-6" />
          </span>
          <div>
            <h2 id="whats-new-title" className="font-display text-[20px] font-bold leading-tight">
              Có gì mới?
            </h2>
            <p className="text-[12px] text-ink-500">Phiên bản {version}</p>
          </div>
        </div>

        {/* Highlights list */}
        <ul className="mb-5 space-y-2.5">
          {HIGHLIGHTS.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-start gap-3">
              <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600">
                <Icon className="h-3.5 w-3.5" />
              </span>
              <span className="text-[13px] leading-snug text-ink-700">{label}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="flex gap-2.5">
          <button
            onClick={onClose}
            className="flex-1 rounded-2xl bg-ink-100 py-3 text-[14px] font-bold text-ink-700 hover:bg-ink-200 transition"
          >
            Đã hiểu
          </button>
          <button
            onClick={onClose}
            className="gbrand flex-1 rounded-2xl py-3 text-[14px] font-bold text-white transition"
          >
            Khám phá
          </button>
        </div>
      </div>
    </div>
  );
}
